/**
 * IMPORTS
 */

import * as Sentry from "@sentry/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { toast } from "react-toastify";

// typings
import {
  ISolicitacaoRemessaPayload,
  ISolicitacaoRemessaResponse,
} from "@/hooks/remessas/solicitar-remessa/index.d";

// store / zustand
import { useStoreZustandUserAuth } from "@/store-zustand/user-auth";

// domain / caso de usos
import { handlerSolicitarRemessa } from "@/domain/use-cases/remessas/solicitar-remessa";
import { formDataToJson } from "@/utils/formdata-to-json";
import {
  getApiErrorHttpStatus,
  getApiErrorMessage,
  getApiErrorResponsePayload,
} from "@/utils/normalize-api-error/normalize-api-error";
// utils
import { notifyAlert } from "@/utils/notify-messages";

/**
 * Toast helpers
 */
const notifySuccess = (message: string) =>
  toast(message, {
    type: "success",
    position: "top-center",
  });

const notifyError = (message: string) =>
  toast(message, {
    type: "error",
    position: "top-center",
  });

export function useSolicitarRemessa() {
  const queryClient = useQueryClient();
  const { token, user } = useStoreZustandUserAuth(); // ← só lemos user

  const mutation = useMutation<ISolicitacaoRemessaResponse, Error, ISolicitacaoRemessaPayload>({
    mutationFn: (payload) => handlerSolicitarRemessa(token!, payload),

    onSuccess: async (response) => {
      if (response?.data?.id) {
        notifySuccess("Remessa solicitada com sucesso!");
        await queryClient.invalidateQueries({
          queryKey: ["vendas-creditos"],
        });
        return;
      }

      console.log("Resposta inesperada ao solicitar remessa:", response);
      notifyError(response?.message ?? "Erro inesperado ao solicitar remessa!");
    },

    onError: (error, payload) => {
      const status = getApiErrorHttpStatus(error);
      const messageNormalized = getApiErrorMessage(error);
      const responsePayload = getApiErrorResponsePayload(error);
      const paylaodFormated = formDataToJson(payload as any);

      const axiosErr = axios.isAxiosError(error) ? error : undefined;

      //  Erro esperado → NÃO manda pro Sentry
      if (status === 422) {
        notifyAlert(messageNormalized);
        return;
      }

      // Erro esperado (500 tratado pelo UX)
      if (status === 500) {
        notifyError(
          (responsePayload as { message?: string } | undefined)?.message ||
            "Erro interno no servidor."
        );

        // MONITORAMENTO (sem alterar UX)
        Sentry.captureException(error, {
          fingerprint: ["solicitar-remessa", String(Date.now().toLocaleString())],
          tags: {
            module: "remessas",
            action: "solicitar-remessa",
            http_status: "500",
          },
          user: user
            ? {
                id: String(user.id),
                email: user.email,
              }
            : undefined,
          extra: {
            payload_enviado: paylaodFormated,
            user_id_payload: paylaodFormated.user_id_solicitante_remessa,
            response: responsePayload,
            url: axiosErr?.config?.url,
            method: axiosErr?.config?.method,
          },
        });

        return;
      }

      //  fallback (erro não esperado)
      notifyError(`Erro inesperado ao solicitar remessa! ${error?.message}`);

      Sentry.captureException(error, {
        fingerprint: ["solicitar-remessa", String(Date.now().toLocaleString())],
        tags: {
          module: "remessas",
          action: "solicitar-remessa",
          http_status: String(status ?? "unknown"),
        },
        user: user
          ? {
              id: String(user.id),
              email: user.email,
            }
          : undefined,
        extra: {
          payload_enviado: paylaodFormated,
          user_id_payload: paylaodFormated.user_id_solicitante_remessa,
          response: responsePayload,
        },
      });
    },
  });

  return {
    solicitarRemessa: mutation.mutate,
    solicitarRemessaAsync: mutation.mutateAsync,
    isLoadingSolicitarRemessa: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
    data: mutation.data,
    isSuccess: mutation.isSuccess,
  };
}
